import tips from '@/renderer/scripts/utils/tips';
import storage from '@/renderer/scripts/utils/storage/index';
import smh, { SMH } from '@/renderer/scripts/smh';
import { WeakAccessTokenManager } from '@/renderer/scripts/libs/smh-sdk-class';
import { getDeviceName } from '@/renderer/scripts/utils/tools';

class ShareAccessTokenManager extends WeakAccessTokenManager<SMH.SpaceToken> {
  private extractionCode: string;
  private cookieShareToken: string;
  private userId: number | undefined;
  constructor(config: SMH.AccessTokenManagerConfig<SMH.SpaceToken>) {
    super(config);
    this.extractionCode = '';
    this.cookieShareToken = '';
  }

  /**
   * @description 从远程获取 token 并存入map
   * @private
   * @param {string} shareToken 分享 token
   * @param {string} extractCode 提取码
   * @memberof ShareAccessTokenManager
   */
  public fetch = async (shareToken: string): Promise<void> => {
    try {
      const deviceId = getDeviceName();
      const res = await smh.userAPI.verifyExtractionCode({
        shareToken,
        deviceId,
        extractionCode: this.extractionCode,
      });
      const tokenItem = {
        ...res.data,
        updateTime: new Date().getTime() / 1000,
      };
      this.tokenMap[shareToken] = tokenItem;
      this.storage.set(this.tokenMap);
    } catch (e) {
      tips.error(e as any);
    }
  };

  /**
   * @description 根据 提取码和分享 Token 获取 AccessToken
   * @param {string} shareToken
   * @param {string} extractionCode
   * @return {*}  {Promise<any>}
   * @memberof ShareAccessTokenManager
   */
  async get(shareToken: string): Promise<any> {
    const tokenItem = this.tokenMap[shareToken];
    // 从cookie中拿到解压码，且cookie中存的shareToken与传入的shareToken一致的情况下，方可进行提取
    if (!this.isAccessTokenValid(tokenItem) && this.extractionCode && shareToken === this.cookieShareToken) {
      await this.fetch(shareToken);
      return {
        ...this.tokenMap[shareToken],
        userId: this.userId,
      };
    }
    return {
      ...tokenItem,
      userId: this.userId,
    };
  }
  set(shareToken: string, params: SMH.GetSpaceInfoData): void {
    const { accessToken, expiresIn, libraryId, spaceId } = params;
    this.tokenMap[shareToken] = {
      accessToken,
      expiresIn,
      libraryId,
      spaceId,
      updateTime: new Date().getTime() / 1000,
    };
    this.storage.set(this.tokenMap);
  }
  setData(params: { extractionCode?: string; userId?: number; cookieShareToken?: string }): void {
    if ('extractionCode' in params) {
      this.extractionCode = params.extractionCode ?? '';
    }
    if ('userId' in params) {
      this.userId = params.userId;
    }
    if ('cookieShareToken' in params) {
      this.cookieShareToken = params.cookieShareToken ?? '';
    }
  }
}
const shareAccessTokenManager = new ShareAccessTokenManager({
  storage: {
    get: () =>
      new Promise(resolve => {
        const tokenMap = storage.get<Record<string, SMH.SpaceToken>>(storage.StoreKey.SHARE_ACCESS_TOKEN_MAP);
        resolve(tokenMap);
      }),
    set: (spaceMap: Record<string, SMH.SpaceToken>) => {
      storage.set(storage.StoreKey.SHARE_ACCESS_TOKEN_MAP, spaceMap);
    },
    del: () => {
      storage.del(storage.StoreKey.SHARE_ACCESS_TOKEN_MAP);
    },
  },
});
smh.initGetInfoFunction({
  getShareInfo: async (shareToken: string, isFetch?: boolean) => {
    if (isFetch) {
      await shareAccessTokenManager.fetch(shareToken);
    }
    return shareAccessTokenManager.get(shareToken);
  },
});
export default shareAccessTokenManager;
